import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, take, takeUntil } from 'rxjs/operators';
import { AssetFault } from 'src/app/models/assetFault';
import { AssetFaultService } from 'src/app/services/asset-fault.service';
import { FaultCodeService } from 'src/app/diagnostics/component/fault-code/fault-code.service';
import { IssuesService } from '../../issues.service';
import {
  FAULT_RANGE_MAX_DAYS, FaultPointVm, FaultRange, FaultSeriesVm, IssueFaultRef, LampRank,
  applyVisibility, axisTicks, buildFaultSeries, clampRange, dayEndMs, dayStartMs,
  defaultRange, episodeRuns, faultNameQualifier, flatSeries, issueFaultRefs, monotonePath,
  niceCeiling, rangeDays, rangeKey, toDateInput,
} from './fault-history-series';

export interface FaultHistoryDialogData {
  assetId: string;
  vin: string;
  assetName: string;
}

interface LineVm { runs: string[]; color: string; tailD: string; edges: EdgeVm[]; }
interface EdgeVm { x: number; y: number; top: number; title: string; }
interface GridLineVm { x1: number; x2: number; y1: number; y2: number; }
interface ValueTickVm { y: number; label: number; }
interface LaneVm { y: number; color: string; blocks: LaneBlockVm[]; }
interface LaneBlockVm { x: number; width: number; lamp: LampRank; title: string; }
interface XTickVm { x: number; label: string; }
interface HoverRowVm {
  name: string; color: string; count: number; seen: number; lamp: LampRank; lampTitle: string;
}

const VIEW_W = 860;
const PAD_L = 42;
const PAD_R = 14;
const PLOT_TOP = 12;
const PLOT_BOTTOM = 232;
const LANE_TOP = 250;
const LANE_H = 11;
const LANE_GAP = 4;
const AXIS_LABEL_DROP = 15;

/**
 * Every fault the truck's engine control modules reported over a window, with the module's
 * own occurrence count against time.
 *
 * Opened from an Issue, and scoped to the asset rather than to that Issue: a reader asking
 * why one fault opened an Issue is usually asking what else the truck was saying at the
 * time. The faults behind an open Issue are ticked, and the rest are one click away.
 *
 * Drawn as plain SVG. The application carries no chart library, the same as the confidence
 * chart in the drawer behind this dialog.
 */
@Component({
  selector: 'app-fault-history-dialog',
  templateUrl: './fault-history-dialog.component.html',
  styleUrls: ['./fault-history-dialog.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FaultHistoryDialogComponent implements OnInit, OnDestroy {
  readonly maxDays = FAULT_RANGE_MAX_DAYS;

  // Bound to the two date boxes, so they hold whatever was typed including nothing. The
  // window actually drawn is `range`, and only a clamped pair ever reaches it.
  startDate = '';
  endDate = '';
  today = '';
  range: FaultRange;

  readonly plotLeft = PAD_L;
  readonly plotRight = VIEW_W - PAD_R;
  readonly plotTop = PLOT_TOP;
  readonly plotBottom = PLOT_BOTTOM;
  readonly laneHeight = LANE_H;

  viewBox = `0 0 ${VIEW_W} 300`;
  labelY = PLOT_BOTTOM + AXIS_LABEL_DROP;

  loading = true;
  failed = false;
  series: FaultSeriesVm[] = [];
  shownCount = 0;
  reportCount = 0;
  lines: LineVm[] = [];
  lanes: LaneVm[] = [];
  dayGrid: GridLineVm[] = [];
  valueGrid: GridLineVm[] = [];
  valueTicks: ValueTickVm[] = [];
  xTicks: XTickVm[] = [];
  flatNote = '';

  // The crosshair reads a report that happened, never a moment the pointer invented.
  hoverX = 0;
  hoverAt = 0;
  hoverRows: HoverRowVm[] = [];
  hoverDots: { cy: number; color: string }[] = [];
  hoverOpen = false;
  // Offset from the left edge of the chart box, in pixels. Measured rather than taken as a
  // fraction of the drawing: the box carries side padding the drawing does not, so a
  // percentage puts the readout up to that padding away from the crosshair it belongs to.
  hoverLeftPx = 0;
  hoverFlip = false;

  // What the reader ticked, by series key, so a change of range does not undo it.
  private readonly chosen = new Map<string, boolean>();
  // Windows already read, by range. A reader who widens and comes back pays once.
  private readonly pages = new Map<string, AssetFault[]>();
  private readonly names = new Map<string, string>();
  private refs: IssueFaultRef[] = [];
  private yMax = 4;
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: FaultHistoryDialogData,
    private readonly dialogRef: MatDialogRef<FaultHistoryDialogComponent>,
    private readonly faults: AssetFaultService,
    private readonly faultCodes: FaultCodeService,
    private readonly issues: IssuesService,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    const now = Date.now();
    this.range = defaultRange(now);
    this.today = toDateInput(now);
    this.syncBoxes();
  }

  ngOnInit(): void {
    // The per-asset list serves open Issues only, so a fault behind an Issue somebody has
    // since closed does not tick. The chart still draws it; it just starts unticked.
    this.issues.listByAsset(this.data.assetId).pipe(
      take(1),
      catchError(() => of([])),
      takeUntil(this.destroy$),
    ).subscribe(list => {
      this.refs = issueFaultRefs(list ?? []);
      this.rebuild();
      this.cdr.markForCheck();
    });
    this.load(this.range);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get spanDays(): number {
    return rangeDays(this.range);
  }

  // A date box hands back an empty string for a cleared box and enforces neither bound while
  // the reader types. So whatever they leave is clamped when they leave it, and the boxes are
  // written back with what the chart actually drew rather than what was typed.
  commitRange(): void {
    const wanted = clampRange(
      { startMs: dayStartMs(this.startDate), endMs: dayEndMs(this.endDate) }, Date.now());
    this.syncBoxes(wanted);
    if (rangeKey(wanted) === rangeKey(this.range)) return;
    this.range = wanted;
    this.load(wanted);
  }

  setAll(visible: boolean): void {
    for (const s of this.series) {
      s.visible = visible;
      this.chosen.set(s.key, visible);
    }
    this.closeHover();
    this.render();
  }

  toggle(series: FaultSeriesVm, visible: boolean): void {
    series.visible = visible;
    this.chosen.set(series.key, visible);
    this.closeHover();
    this.render();
  }

  trackSeries(_index: number, series: FaultSeriesVm): string {
    return series.key;
  }

  // The code a technician looks up, in the words of its protocol. J1708 numbers its
  // parameters as PID or SID, not SPN, which is how the drawer's evidence line reads too.
  private codeLabel(series: FaultSeriesVm): string {
    switch (series.protocol) {
      case 'OBDII':
        return this.translate.instant('@issueFaultCodeObd',
          { code: series.description || '', address: series.controller });
      case 'J1708':
        return this.translate.instant('@issueFaultCodeJ1708',
          { code: series.faultCode, fmi: series.failureMode });
      default:
        return this.translate.instant('@issueFaultCodeBus',
          { spn: series.faultCode, fmi: series.failureMode });
    }
  }

  private lampTitle(lamp: LampRank): string {
    return this.translate.instant(
      ['@issueFaultLampNone', '@issueFaultLampMalfunction',
        '@issueFaultLampWarning', '@issueFaultLampStop'][lamp]);
  }

  close(): void {
    this.dialogRef.close();
  }

  private syncBoxes(range: FaultRange = this.range): void {
    this.startDate = toDateInput(range.startMs);
    // The window runs to the end of the day the reader named, so the box shows that day
    // rather than the midnight after it.
    this.endDate = toDateInput(range.endMs - 1);
  }

  private load(range: FaultRange): void {
    const held = this.pages.get(rangeKey(range));
    if (held) {
      this.rebuildFor(range, held);
      return;
    }
    this.loading = true;
    this.failed = false;
    this.cdr.markForCheck();

    this.faults.getFaultsInRange(this.data.vin, new Date(range.startMs), new Date(range.endMs))
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: rows => {
          this.pages.set(rangeKey(range), rows ?? []);
          // Two reads can be in flight when the reader moves a date twice. The slower one
          // still fills the cache, and only the window they are on reaches the chart.
          if (rangeKey(range) === rangeKey(this.range)) this.rebuildFor(range, rows ?? []);
        },
        error: () => {
          if (rangeKey(range) !== rangeKey(this.range)) return;
          this.loading = false;
          this.failed = true;
          this.series = [];
          this.render();
        },
      });
  }

  private rebuildFor(range: FaultRange, rows: AssetFault[]): void {
    this.failed = false;
    this.loading = false;
    this.range = range;
    this.series = applyVisibility(buildFaultSeries(rows, this.refs, range), this.chosen);
    this.resolveNames();
    this.closeHover();
    this.render();
  }

  // Rebuild against the window already loaded. Used when the Issue list lands after the
  // faults have, which is the ordinary case: the two reads race and either can win.
  private rebuild(): void {
    const rows = this.pages.get(rangeKey(this.range));
    if (rows) this.rebuildFor(this.range, rows);
  }

  // A J1939 row carries no description of its own, so without this every line in the list
  // is blank. One read per distinct code, and the answers are kept for the whole dialog.
  private resolveNames(): void {
    this.applyLabels();

    const wanted = [...new Set(this.series
      .map(s => s.faultCodeId)
      .filter(id => !!id && !this.names.has(id)))];
    if (!wanted.length) return;

    const reads: Record<string, Observable<{ name?: string } | null>> = {};
    for (const id of wanted) {
      reads[id] = this.faultCodes.getDataById(id).pipe(catchError(() => of(null))) as
        Observable<{ name?: string } | null>;
    }
    forkJoin(reads).pipe(take(1), takeUntil(this.destroy$)).subscribe(answers => {
      for (const id of wanted) this.names.set(id, answers[id]?.name ?? '');
      this.applyLabels();
      this.render();
    });
  }

  // The catalog names a fault by its code alone, so two failure modes of one code arrive
  // under the same name. The qualifier is what separates them, and it goes in the name
  // rather than only in the code column, because that column is not on a narrow screen and
  // has never been in the hover readout.
  private applyLabels(): void {
    for (const s of this.series) {
      s.name = this.names.get(s.faultCodeId) || s.description;
      s.codeLabel = this.codeLabel(s);
      // Held on the row as well as folded into the label: the list renders it as its own
      // element, so a narrow column cannot make it the part that gets cut.
      const qualifier = faultNameQualifier(s);
      s.qualifier = qualifier;
      s.label = !s.name
        // A catalog read that answered with nothing still has to leave something to read.
        ? s.codeLabel
        : qualifier
          ? this.translate.instant('@issueFaultNameQualified', { name: s.name, qualifier })
          : s.name;
      s.worstLampTitle = this.translate.instant('@issueFaultWorstLamp',
        { lamp: this.lampTitle(s.worstLamp) });
      s.episodesTitle = this.translate.instant('@issueFaultEpisodesTitle',
        { count: s.episodes });
    }
  }

  private render(): void {
    const shown = this.series.filter(s => s.visible);
    this.shownCount = shown.length;
    this.reportCount = this.series.reduce((n, s) => n + s.points.length, 0);
    this.xTicks = axisTicks(this.range, this.lang)
      .map(t => ({ x: this.r(this.xOf(t.atMs)), label: t.label }));

    // The lanes sit under the plot and the axis sits under them, so the drawing grows and
    // shrinks with how many faults are ticked.
    const laneCount = shown.length;
    const laneBottom = LANE_TOP + laneCount * (LANE_H + LANE_GAP);
    this.labelY = (laneCount ? laneBottom : PLOT_BOTTOM) + AXIS_LABEL_DROP;
    this.viewBox = `0 0 ${VIEW_W} ${this.labelY + 12}`;

    this.dayGrid = this.xTicks.map(t => ({ x1: t.x, x2: t.x, y1: PLOT_TOP, y2: PLOT_BOTTOM }));

    if (!shown.length) {
      this.lines = [];
      this.lanes = [];
      this.valueGrid = [];
      this.valueTicks = [];
      this.flatNote = '';
      this.cdr.markForCheck();
      return;
    }

    this.yMax = niceCeiling(Math.max(
      ...shown.map(s => Math.max(...s.points.map(p => p.occurrenceCount)))));
    this.valueTicks = [0, 1, 2, 3, 4].map(i => {
      const label = Math.round((this.yMax / 4) * i);
      return { y: this.r(this.yOf(label)), label };
    });
    this.valueGrid = this.valueTicks.map(t => ({
      x1: PAD_L, x2: VIEW_W - PAD_R, y1: t.y, y2: t.y,
    }));

    this.lines = shown.map(s => ({
      color: s.color,
      // One path per run, and a gap between them. See episodeRuns.
      runs: episodeRuns(s.points).map(run => monotonePath(
        run.map(p => this.xOf(p.atMs)), run.map(p => this.yOf(p.occurrenceCount)))),
      tailD: this.tailOf(s),
      edges: s.points.filter(p => p.restart).map(p => ({
        x: this.r(this.xOf(p.atMs)),
        y: this.r(this.yOf(p.occurrenceCount)),
        top: this.r(this.yOf(p.occurrenceCount)) - 9,
        title: this.translate.instant('@issueFaultRestartAt',
          { name: s.label, time: this.stamp(p.atMs) }),
      })),
    }));

    this.lanes = shown.map((s, i) => ({
      y: LANE_TOP + i * (LANE_H + LANE_GAP),
      color: s.color,
      blocks: this.laneBlocks(s),
    }));

    const flat = flatSeries(this.series);
    this.flatNote = !flat.length ? ''
      : flat.length === 1
        ? this.translate.instant('@issueFaultFlatOne',
          { name: flat[0].label, count: flat[0].points.length })
        : this.translate.instant('@issueFaultFlatMany', { count: flat.length });
    this.cdr.markForCheck();
  }

  // The lamp the module lit, held until the next report says otherwise. An active fault
  // carries its last lamp to the right edge, the same as the count above it does.
  private laneBlocks(series: FaultSeriesVm): LaneBlockVm[] {
    return series.points.map((point, i) => {
      const x = this.xOf(point.atMs);
      const next = i + 1 < series.points.length
        ? this.xOf(series.points[i + 1].atMs)
        : (series.active ? VIEW_W - PAD_R : x + 2);
      return {
        x: this.r(x),
        width: this.r(Math.max(1.5, next - x)),
        lamp: point.lamp,
        title: this.translate.instant('@issueFaultLampAt', {
          name: series.label,
          time: this.stamp(point.atMs),
          lamp: this.lampTitle(point.lamp),
        }),
      };
    });
  }

  // A fault the record still calls active did not stop existing when the truck stopped
  // reporting it. The count carries to the right edge, dashed, so the line and the Active
  // chip in the list say the same thing. An inactive fault ends where it ended.
  private tailOf(series: FaultSeriesVm): string {
    if (!series.active) return '';
    const last = series.points[series.points.length - 1];
    const x = this.xOf(last.atMs);
    if (x >= VIEW_W - PAD_R - 1) return '';
    const y = this.r(this.yOf(last.occurrenceCount));
    return `M ${this.r(x)} ${y} L ${this.r(VIEW_W - PAD_R)} ${y}`;
  }

  onPointerMove(event: MouseEvent): void {
    const chart = event.currentTarget as HTMLElement;
    const svg = chart.querySelector('svg');
    const shown = this.series.filter(s => s.visible);
    if (!svg || !shown.length) { this.closeHover(); return; }

    const box = svg.getBoundingClientRect();
    if (!box.width) { this.closeHover(); return; }
    const scale = VIEW_W / box.width;
    const vx = (event.clientX - box.left) * scale;
    if (vx < PAD_L || vx > VIEW_W - PAD_R) { this.closeHover(); return; }

    // Snap to a report the truck actually sent.
    let atMs = 0;
    let best = Infinity;
    for (const s of shown) {
      for (const p of s.points) {
        const dx = Math.abs(this.xOf(p.atMs) - vx);
        if (dx < best) { best = dx; atMs = p.atMs; }
      }
    }
    if (!atMs) { this.closeHover(); return; }
    // The pointer moves many times between one report and the next. Nothing to redo until
    // it snaps to a different one.
    if (this.hoverOpen && atMs === this.hoverAt) return;

    const rows: HoverRowVm[] = [];
    const dots: { cy: number; color: string }[] = [];
    for (const s of shown) {
      const p = this.valueAt(s, atMs);
      if (!p) continue;
      rows.push({
        name: s.label, color: s.color, count: p.occurrenceCount,
        seen: p.instanceCount, lamp: p.lamp, lampTitle: this.lampTitle(p.lamp),
      });
      dots.push({ cy: this.r(this.yOf(p.occurrenceCount)), color: s.color });
    }

    this.hoverAt = atMs;
    this.hoverX = this.r(this.xOf(atMs));
    this.hoverRows = rows;
    this.hoverDots = dots;
    this.hoverOpen = rows.length > 0;
    const chartBox = chart.getBoundingClientRect();
    this.hoverLeftPx = Math.round(box.left - chartBox.left + this.hoverX / scale);
    // Past two thirds of the box the readout would run off the right edge, so it swaps to
    // the other side of the crosshair.
    this.hoverFlip = chartBox.width > 0 && this.hoverLeftPx / chartBox.width > 0.62;
    this.cdr.markForCheck();
  }

  closeHover(): void {
    if (!this.hoverOpen) return;
    this.hoverOpen = false;
    this.hoverRows = [];
    this.hoverDots = [];
    this.cdr.markForCheck();
  }

  // The count the module last reported at or before this moment. Null before a series has
  // started, because the chart must not claim a value the truck had not sent yet.
  private valueAt(series: FaultSeriesVm, atMs: number): FaultPointVm | null {
    let hit: FaultPointVm | null = null;
    for (const p of series.points) {
      if (p.atMs <= atMs) hit = p; else break;
    }
    return hit;
  }

  private stamp(atMs: number): string {
    return new Date(atMs).toLocaleString(this.lang,
      { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  private get lang(): string {
    return this.translate.currentLang || 'en-US';
  }

  private xOf(atMs: number): number {
    const span = this.range.endMs - this.range.startMs;
    const ratio = span > 0 ? (atMs - this.range.startMs) / span : 0;
    const clamped = Math.min(1, Math.max(0, ratio));
    return PAD_L + clamped * (VIEW_W - PAD_L - PAD_R);
  }

  private yOf(count: number): number {
    const ratio = this.yMax > 0 ? Math.min(1, Math.max(0, count / this.yMax)) : 0;
    return PLOT_TOP + (1 - ratio) * (PLOT_BOTTOM - PLOT_TOP);
  }

  // SVG coordinates carry no meaning past a tenth of a unit, and the full float makes the
  // rendered markup unreadable when something needs looking at.
  private r(value: number): number {
    return Math.round(value * 10) / 10;
  }
}
